/**
 * @file InstallPrompt.tsx
 * @description Modal educativo para instalar la PWA
 * Aparece una vez para usuarios nuevos que pueden instalar
 * @author XiaVan Team
 */

import { useState, useEffect } from 'react'
import { X, Smartphone, Zap, WifiOff } from 'lucide-react'
import { useInstallPrompt } from '@/hooks/useInstallPrompt'

export default function InstallPrompt() {
  const { isInstallable, promptInstall } = useInstallPrompt()
  const [isVisible, setIsVisible] = useState(false)
  const [hasBeenShown, setHasBeenShown] = useState(false)

  useEffect(() => {
    // Check if prompt has been shown before
    const shown = localStorage.getItem('install-prompt-shown')
    if (shown) {
      setHasBeenShown(true)
      return
    }

    // Show prompt after 10 seconds if installable
    if (isInstallable && !hasBeenShown) {
      const timer = setTimeout(() => {
        setIsVisible(true)
      }, 10000)

      return () => clearTimeout(timer)
    }
  }, [isInstallable, hasBeenShown])

  const handleInstall = async () => {
    const accepted = await promptInstall()
    
    if (accepted) {
      console.log('✅ PWA installed successfully')
    }
    
    // Mark as shown regardless of outcome
    localStorage.setItem('install-prompt-shown', 'true')
    setHasBeenShown(true)
    setIsVisible(false)
  }

  const handleDismiss = () => {
    localStorage.setItem('install-prompt-shown', 'true')
    setHasBeenShown(true)
    setIsVisible(false)
  }

  if (!isVisible || !isInstallable) {
    return null
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/50 p-4 sm:items-center">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl dark:bg-gray-800">
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute right-4 top-4 rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
          aria-label="Cerrar"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Logo */}
        <div className="mb-4 flex justify-center">
          <img src="/logo.png" alt="XiaVan" className="h-16 w-16" />
        </div>

        {/* Title */}
        <h2 className="mb-2 text-center text-2xl font-bold text-gray-900 dark:text-white">
          ¡Instala XiaVan!
        </h2>

        <p className="mb-6 text-center text-gray-600 dark:text-gray-400">
          Accede más rápido a tus escapadas en camper
        </p>

        {/* Benefits */}
        <div className="mb-6 space-y-3">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-blue-100 p-2 dark:bg-blue-900/30">
              <Zap className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 dark:text-white">
                Acceso instantáneo
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Abre desde tu pantalla de inicio como una app nativa
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-purple-100 p-2 dark:bg-purple-900/30">
              <WifiOff className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 dark:text-white">
                Funciona sin conexión
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Consulta tus planes guardados aunque no haya internet
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-green-100 p-2 dark:bg-green-900/30">
              <Smartphone className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 dark:text-white">
                Ocupa muy poco espacio
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Menos de 5MB, mucho más ligera que una app tradicional
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2">
          <button
            onClick={handleInstall}
            className="w-full rounded-lg bg-blue-600 px-4 py-3 font-semibold text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:bg-blue-500 dark:hover:bg-blue-600"
          >
            Instalar ahora
          </button>
          <button
            onClick={handleDismiss}
            className="w-full rounded-lg px-4 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
          >
            Quizás más tarde
          </button>
        </div>
      </div>
    </div>
  )
}
